import React, { useCallback, useState, useMemo } from 'react';
import { Modal, Image, Button, Row, Col } from 'antd';
import { CloseOutlined, FileImageOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { Media } from '../../types/media.types';
import { useTranslation } from 'react-i18next';

interface ImagePreviewModalProps {
  open: boolean;
  onClose: () => void;
  imageUrls: string[];
}

interface VideoPreviewModalProps {
  open: boolean;
  onClose: () => void;
  videoUrls: string[];
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({ open, onClose, imageUrls }) => {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closeIcon={<CloseOutlined />}
      width={800}
      centered
      destroyOnClose
    >
      <Row gutter={[16, 16]} justify="space-evenly">
        {imageUrls.map((url, index) => (
          <Col span={6} key={index}>
            <Image
              src={url}
              alt={`${index}`}
              style={{
                width: '100%',
                height: '10em',
                objectFit: 'cover',
              }}
              fallback="/fallback.svg"
              preview={{
                mask: t('common.mediaPicker.preview'),
              }}
            />
          </Col>
        ))}
      </Row>
    </Modal>
  );
};

const VideoPreviewModal: React.FC<VideoPreviewModalProps> = ({ open, onClose, videoUrls }) => {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closeIcon={<CloseOutlined />}
      width={800}
      centered
      destroyOnClose
    >
      <Row gutter={[16, 16]} justify="space-evenly">
        {videoUrls.map((url, index) => (
          <Col span={12} key={index}>
            <video
              controls
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '400px',
                borderRadius: '8px',
              }}
              preload="metadata"
            >
              <source src={url} type="video/mp4" />
              {t('common.mediaPicker.videoNotSupported')}
            </video>
          </Col>
        ))}
      </Row>
    </Modal>
  );
};

export interface MediaPreviewProps {
  mediaArray?: Media[];
  mediaUrls?: string[];
}

type MediaType = 'image' | 'video' | 'file' | 'mixed' | 'unknown';

const MediaPreview: React.FC<MediaPreviewProps> = ({ mediaArray, mediaUrls }) => {
  const { t } = useTranslation();
  const [showMediaPreview, setShowMediaPreview] = useState(false);

  const urls = useMemo(() => {
    if (mediaUrls) return mediaUrls;
    if (mediaArray) return mediaArray.map((media) => media.url);
    return [];
  }, [mediaArray, mediaUrls]);

  const mediaType: MediaType = useMemo(() => {
    if (mediaUrls) return 'image'; // Assume mediaUrls are always images
    if (!mediaArray || mediaArray.length === 0) return 'unknown';

    const mediaTypesInArray: MediaType[] = [];
    mediaArray.forEach((media) => {
      if (media.mimeType.includes('image')) {
        mediaTypesInArray.push('image');
      } else if (media.mimeType.includes('video')) {
        mediaTypesInArray.push('video');
      } else if (media.mimeType.includes('file')) {
        mediaTypesInArray.push('file');
      }
    });
    const uniqueMediaTypes = mediaTypesInArray.filter(
      (type, index, self) => self.indexOf(type) === index
    );
    const result = uniqueMediaTypes.length === 1 ? uniqueMediaTypes[0] : 'mixed';
    return result;
  }, [mediaArray, mediaUrls]);

  const { imageUrls, videoUrls } = useMemo(() => {
    if (!mediaArray) return { imageUrls: [], videoUrls: [] };

    const images: string[] = [];
    const videos: string[] = [];

    mediaArray.forEach((media) => {
      if (media.mimeType.includes('image')) {
        images.push(media.url);
      } else if (media.mimeType.includes('video')) {
        videos.push(media.url);
      }
    });

    return { imageUrls: images, videoUrls: videos };
  }, [mediaArray]);

  const renderMedia = useCallback(() => {
    if (mediaType === 'image' || (mediaType === 'mixed' && imageUrls.length > 0)) {
      return (
        <ImagePreviewModal
          open={showMediaPreview}
          onClose={() => {
            setShowMediaPreview(false);
          }}
          imageUrls={imageUrls.length > 0 ? imageUrls : urls}
        />
      );
    }

    if (mediaType === 'video' || (mediaType === 'mixed' && videoUrls.length > 0)) {
      return (
        <VideoPreviewModal
          open={showMediaPreview}
          onClose={() => {
            setShowMediaPreview(false);
          }}
          videoUrls={videoUrls}
        />
      );
    }

    return null;
  }, [mediaType, urls, showMediaPreview, imageUrls, videoUrls]);

  const getButtonIcon = () => {
    if (mediaType === 'video') return <PlayCircleOutlined />;
    return <FileImageOutlined />;
  };

  const getButtonText = () => {
    if (mediaType === 'video') {
      return t('common.mediaPreview.video', { count: urls.length });
    }
    return t('common.mediaPreview.default', { count: urls.length });
  };

  return (
    <div>
      {urls.length > 0 && (
        <>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            icon={getButtonIcon()}
            onClick={() => {
              setShowMediaPreview(true);
            }}
          >
            {getButtonText()}
          </Button>
          {renderMedia()}
        </>
      )}
    </div>
  );
};

export default MediaPreview;
